import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, Building2, FolderKanban, Landmark, FileStack, Search, Bell, Shield, Settings2, LogOut, Menu, X, CreditCard, School, Rocket, FileSignature, Gavel } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Logo, PartnerMark } from "@/components/PublicLayout";

const LINKS = [
  ["/app", "Dashboard", LayoutDashboard], ["/app/business", "Business profile", Building2], ["/app/projects", "Projects", FolderKanban],
  ["/app/funding", "Funding", Landmark], ["/app/applications", "Applications", FileStack], ["/app/ip-search", "IP search tools", Search],
  ["/app/westmont", "Westmont Hub", School], ["/app/programme", "i2i programme", Rocket], ["/app/hub-documents", "Hub documents", FileSignature], ["/app/panels", "Panel reviews", Gavel], ["/app/alerts", "Alerts", Bell], ["/app/billing", "Plan & access", CreditCard], ["/app/security", "Security", Shield],
];

const OrgSwitcher = () => {
  const { orgs, orgId, setOrgId } = useAuth();
  if (orgs.length < 1) return null;
  return (
    <div className="px-3 pb-4">
      <label htmlFor="org-switch" className="caption mb-1 block">Workspace</label>
      <select id="org-switch" data-testid="org-switcher" value={orgId || ""} onChange={(e) => setOrgId(e.target.value)} className="input py-2">
        {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
      <Link to="/app/onboarding" data-testid="new-workspace-link" className="mt-1.5 inline-block text-xs font-medium text-[#1F8A8B] hover:underline">+ New workspace</Link>
    </div>
  );
};

const SideNav = ({ unread, onNav }) => {
  const { user } = useAuth();
  const links = user?.is_admin ? [...LINKS, ["/app/admin", "Admin console", Settings2]] : LINKS;
  return (
    <nav aria-label="Platform" className="space-y-0.5 px-3">
      {links.map(([to, l, Icon]) => (
        <NavLink key={to} to={to} end={to === "/app"} onClick={onNav} data-testid={`sidebar-${l.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-[#E8F6F6] font-semibold text-[#1F8A8B]" : "text-slate-600 hover:bg-slate-50 hover:text-[#1E2A33]"}`}>
          <Icon className="h-4 w-4" aria-hidden /> {l}
          {l === "Alerts" && unread > 0 && <span data-testid="unread-count" className="ml-auto rounded-full bg-[#C22033] px-1.5 text-[10px] font-bold text-white">{unread}</span>}
        </NavLink>
      ))}
    </nav>
  );
};

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    api.get("/notifications").then(({ data }) => setUnread(data.filter((n) => !n.read).length)).catch(() => {});
  }, [loc.pathname]);
  const side = (
    <div className="flex h-full flex-col border-r border-slate-200 bg-white py-5">
      <div className="px-6 pb-6"><Logo /></div>
      <OrgSwitcher />
      <SideNav unread={unread} onNav={() => setOpen(false)} />
      <div className="mt-auto border-t border-slate-200 px-6 pt-4">
        <div className="mb-4"><PartnerMark compact /></div>
        <p className="truncate text-sm text-slate-700" data-testid="current-user-email">{user?.email}</p>
        <button onClick={logout} data-testid="logout-button" className="mt-2 inline-flex items-center gap-2 text-xs text-slate-500 hover:text-[#C22033]"><LogOut className="h-3.5 w-3.5" /> Sign out</button>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen bg-[#F8FAFC] lg:pl-64">
      <aside className="fixed inset-y-0 left-0 hidden w-64 lg:block">{side}</aside>
      <div className="glass sticky top-0 z-30 flex h-14 items-center justify-between border-b border-slate-200 px-4 lg:hidden">
        <Logo small />
        <button aria-label="Open navigation" data-testid="app-mobile-menu" className="text-[#1E2A33]" onClick={() => setOpen(true)}><Menu /></button>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72">{side}</div>
          <button aria-label="Close navigation" className="absolute right-4 top-4 text-white" onClick={() => setOpen(false)}><X /></button>
        </div>
      )}
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-12 lg:py-12">{children}</main>
    </div>
  );
}
